import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { MedicalDepartmentAdditionsPopup } from "../../../components/MedicalDepartmentAdditionsPopup/MedicalDepartmentAdditionsPopup";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { DepartmentLocation } from "../../../utils/models";
import { useLocationCredentialsQueryHook } from "../../Profile/v2/SynergyCredentials/credentialsHooks";
import { FacilityScheduleExportButton } from "../FacilityScheduleExport/FacilityScheduleExportButton";
import { FacilityScheduleGridCell } from "./FacilityScheduleGridCell";

const WEEKS_TO_SHOW = 6;
const DAY_HEADER_HEIGHT = 28;

type Props = {
	location: DepartmentLocation;
};

export const FacilityScheduleGrid: FC<Props> = ({ location }) => {
	const { hospitalData, timezone } = useHospitalData();

	const { credentialedNurseIds } = useLocationCredentialsQueryHook({ locationId: location.id, onlyShowApproved: true });

	const [openCellDate, setOpenCellDate] = useState<DateTime | null>(null);
	const [pendingCellDate, setPendingCellDate] = useState<DateTime | null>(null);
	const [showSwitchWarning, setShowSwitchWarning] = useState(false);
	const hasUnsavedChangesRef = useRef(false);

	// hospitalData weeks run Sun–Sat (days[0]=Sun … days[6]=Sat), so we start from Sunday.
	const startOfCurrentWeek = useMemo(() => {
		const now = DateTime.now().setZone(timezone);
		return now.minus({ days: now.weekdayISO }).startOf("day");
	}, [timezone]);

	// Column labels derived from the actual week start so they always match Sun–Sat order.
	const columnHeaders = useMemo(() => Array.from({ length: 7 }, (_, i) => startOfCurrentWeek.plus({ days: i }).toFormat("ccc")), [startOfCurrentWeek]);

	const weeksData = useMemo(() => {
		return Array.from({ length: WEEKS_TO_SHOW }, (_, weekOffset) => {
			const weekStart = startOfCurrentWeek.plus({ weeks: weekOffset });
			const matchedWeek = hospitalData.weeks.find((week) => {
				const firstDay = week.weeklyShifts[0]?.days[0]?.date;
				const lastDay = week.weeklyShifts[0]?.days[6]?.date;
				return firstDay && lastDay && weekStart >= firstDay.startOf("day") && weekStart <= lastDay.startOf("day");
			});
			return { weekStart, matchedWeek };
		});
	}, [startOfCurrentWeek, hospitalData.weeks]);

	const cellData = useMemo(() => {
		return weeksData.map(({ weekStart, matchedWeek }) =>
			Array.from({ length: 7 }, (_, dayIndex) => {
				const date = weekStart.plus({ days: dayIndex });
				if (!matchedWeek) return { date, nurses: [] };
				const nurses = matchedWeek.weeklyShifts
					.flatMap((shift) => shift.days[dayIndex]?.nurses ?? [])
					.filter((nurse) => nurse.location === location.location && !nurse.isPending && !nurse.isPendingToDelete);
				return { date, nurses };
			}),
		);
	}, [weeksData, location.location]);

	const handleCellClick = useCallback(
		(date: DateTime) => {
			if (openCellDate?.toISODate() === date.toISODate()) {
				// Toggle close if no unsaved changes
				if (!hasUnsavedChangesRef.current) {
					setOpenCellDate(null);
				}
				return;
			}
			if (openCellDate !== null && hasUnsavedChangesRef.current) {
				setPendingCellDate(date);
				setShowSwitchWarning(true);
			} else {
				hasUnsavedChangesRef.current = false;
				setOpenCellDate(date);
			}
		},
		[openCellDate],
	);

	const handlePopupClose = useCallback(() => {
		setOpenCellDate(null);
		hasUnsavedChangesRef.current = false;
	}, []);

	const handleDidChange = useCallback(() => {
		hasUnsavedChangesRef.current = true;
	}, []);

	const handleSwitchConfirm = useCallback(() => {
		setShowSwitchWarning(false);
		hasUnsavedChangesRef.current = false;
		setOpenCellDate(pendingCellDate);
		setPendingCellDate(null);
	}, [pendingCellDate]);

	const handleSwitchCancel = useCallback(() => {
		setShowSwitchWarning(false);
		setPendingCellDate(null);
	}, []);

	return (
		<Box sx={{ minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<FacilityScheduleExportButton location={location} />
			</Box>
			<Box sx={{ flex: 1, overflow: "auto" }}>
				{showSwitchWarning && (
					<SystemDialog
						open={true}
						onClose={handleSwitchCancel}
						title={"Discard unsaved changes?"}
						body={"Your changes will be discarded if you switch to another cell."}
						onConfirm={handleSwitchConfirm}
						cancel={"Keep Editing"}
						confirm={"Discard Changes"}
					/>
				)}

				{openCellDate !== null && (
					<MedicalDepartmentAdditionsPopup
						key={openCellDate.toISODate()}
						mode="add-single"
						facility={location}
						clickedDate={openCellDate}
						onClose={handlePopupClose}
						hideCreateShiftsTab={true}
						onDidChange={handleDidChange}
					/>
				)}

				<Box
					sx={{
						display: "grid",
						position: "relative",
						gridTemplateColumns: "repeat(7, 1fr)",
						alignItems: "start",
						gap: "2px",
						width: "100%",
					}}
				>
					{columnHeaders.map((label) => (
						<Box
							key={label}
							sx={{
								position: "sticky",
								top: 0,
								height: DAY_HEADER_HEIGHT,
								background: (theme) => alpha(theme.palette.primary.main, 0.08),
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "primary.dark",
								fontSize: "14px",
								fontWeight: 400,
							}}
						>
							{label.substring(0, 3)}
						</Box>
					))}

					{cellData.map((days) =>
						days.map(({ date, nurses }) => (
							<FacilityScheduleGridCell
								key={date.toISODate()}
								date={date}
								nurses={nurses}
								isOpen={openCellDate?.toISODate() === date.toISODate()}
								credentialedNurseIds={credentialedNurseIds}
								onCellClick={handleCellClick}
							/>
						)),
					)}
				</Box>
			</Box>
		</Box>
	);
};
